import { html, View } from 'rune-ts';
import { classes } from '@services/MarppleShop/shared/util';
import klass from './ShippingDepartureDayView.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { ChangeCalendarEvent } from '@services/MarppleShop/marppleShopStudioApp/components/cells/StudioCalendar/StudioCalendar.ts';
import { Input_Date_forShipDate } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Date_forShipDate.ts';
import format from 'date-fns/format/index.js';
import { ko } from 'date-fns/locale/index.js';

class RadioView extends View<{
  state: 'Normal' | 'Selected';
  as?: string;
}> {
  protected override template({ state }: { state: 'Normal' | 'Selected' }) {
    if (state === 'Normal') {
      return html`<span class="${classes(klass.radio, klass.state_normal)}"></span>`;
    } else if (state === 'Selected') {
      return html`<span class="${classes(klass.radio, klass.state_selected)}">
        <span class="${klass.radio_button}"></span>
      </span>`;
    } else {
      throw new Error();
    }
  }
}

type TProps = {
  isVisible: boolean;
  ship_date: string | null | undefined;
  onShipDateChange: (date: string | null) => void;
};

export class ShippingDepartureDayView extends View<TProps> {
  private isSpecificDate: boolean = Boolean(this.data.ship_date);

  setIsVisible(isVisible: boolean) {
    this.data.isVisible = isVisible;
    this.element().style.display = isVisible ? 'flex' : 'none';
  }

  protected override onRender() {
    this.delegate('click', '#same_as_shipping_policy_radio_button', () => {
      this.isSpecificDate = false;
      this.data.onShipDateChange(null);
      this.redraw();
    });

    this.delegate('click', '#same_as_shipping_policy_radio_text', () => {
      this.isSpecificDate = false;
      this.data.onShipDateChange(null);
      this.redraw();
    });

    this.delegate('click', '#specific_date_radio_button', () => {
      this.isSpecificDate = true;
      this.data.onShipDateChange(this.data.ship_date ?? new Date().toISOString());
      this.redraw();
    });

    this.delegate('click', '#specific_date_radio_text', () => {
      this.isSpecificDate = true;
      this.data.onShipDateChange(this.data.ship_date ?? new Date().toISOString());
      this.redraw();
    });

    this.delegate(ChangeCalendarEvent, Input_Date_forShipDate, (event) => {
      const nextShipDate = String(event.detail.start_date);

      this.data.ship_date = nextShipDate;
      this.subView(Input_Date_forShipDate)!.setDate(event.detail.start_date);
      this.element().querySelector(`.${klass.input_date_help_text}`)!.textContent = `${format(
        new Date(nextShipDate),
        'yyyy-MM-dd(EEE)',
        {
          locale: ko,
        },
      )} 부터 주문순으로 출발 예정`;
      this.data.onShipDateChange(nextShipDate);
    });
  }

  protected override template({ ship_date, isVisible }: TProps) {
    const selectedShipDate = ship_date ? new Date(ship_date) : new Date();

    return html`<div
      class="${classes(klass.shipping_departure_day, {
        [klass.invisible]: !isVisible,
      })}"
    >
      <div class="${klass.label_title}">
        <span class="${classes(klass.label_text, shareFont('14_bold'))}">배송 출발일</span>

        <span class="${klass.required_mark}">
          <span class="${klass.required_mark_icon}"></span>
        </span>
      </div>

      <div class="${klass.radio_wrapper}">
        <div class="${klass.control}">
          <button
            type="button"
            id="same_as_shipping_policy_radio_button"
            class="${klass.radio_button_wrapper}"
          >
            ${new RadioView({
              state: this.isSpecificDate ? 'Normal' : 'Selected',
            })}
          </button>

          <button
            type="button"
            id="same_as_shipping_policy_radio_text"
            class="${classes(klass.radio_text, shareFont('14_medium'))}"
          >
            배송 정책과 동일
          </button>
        </div>

        <div class="${klass.control}">
          <button type="button" id="specific_date_radio_button" class="${klass.radio_button_wrapper}">
            ${new RadioView({
              state: this.isSpecificDate ? 'Selected' : 'Normal',
            })}
          </button>

          <button
            type="button"
            id="specific_date_radio_text"
            class="${classes(klass.radio_text, shareFont('14_medium'))}"
          >
            특정 날짜
          </button>
        </div>
      </div>

      ${this.isSpecificDate
        ? html`<div class="${klass.input_date_picker}">
            ${new Input_Date_forShipDate({
              name: formKeyMap.ship_date,
              value: format(selectedShipDate, 'yyyy-MM-dd'),
              title: '',
              description: '',
            })}

            <p class="${classes(klass.input_date_help_text, shareFont('12_medium'))}">
              ${`${format(selectedShipDate, 'yyyy-MM-dd(EEE)', {
                locale: ko,
              })} 부터 주문순으로 출발 예정`}
            </p>
          </div>`
        : null}
    </div>`;
  }
}
